import React, { useState, useEffect } from 'react';
import { VitalityResponses, DomainResult } from '../../types/index';

interface VitalityAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
}

const VitalityAssessment: React.FC<VitalityAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'weight_loss' | 'appetite_loss' | 'results' | 'carer_correction' | 'completed'>('intro');
  const [responses, setResponses] = useState<VitalityResponses>({});
  const [carerCorrection, setCarerCorrection] = useState({
    weightCorrect: null as boolean | null,
    appetiteCorrect: null as boolean | null
  });

  // 评估逻辑实现
  const evaluateVitality = (responses: VitalityResponses): DomainResult => {
    const weightLossPresent = responses.weightLoss === true;
    const appetiteLossPresent = responses.appetiteLoss === true;
    
    const anyNutritionProblem = weightLossPresent || appetiteLossPresent;
    
    return {
      status: anyNutritionProblem ? 'FAIL' : 'PASS',
      completionMethod: 'full_test',
      testResults: [
        {
          name: "体重减轻评估",
          passed: !weightLossPresent,
          response: responses.weightLoss,
          rawUserAnswers: {
            weightLoss: responses.weightLoss,
            weightLossPresent: weightLossPresent
          }
        },
        {
          name: "食欲减退评估", 
          passed: !appetiteLossPresent,
          response: responses.appetiteLoss,
          rawUserAnswers: {
            appetiteLoss: responses.appetiteLoss,
            appetiteLossPresent: appetiteLossPresent
          }
        }
      ],
      recommendations: anyNutritionProblem ?
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C7-S7.2-CommunityIntervention"] :
        ["ICOPE-C3-S3.1.3-Advice-HealthyDiet"]
    };
  };

  const handleWeightLoss = (hasWeightLoss: boolean) => {
    setResponses(prev => ({ ...prev, weightLoss: hasWeightLoss }));
    setCurrentStep('appetite_loss');
  };

  const handleAppetiteLoss = (hasAppetiteLoss: boolean) => {
    const updatedResponses = { ...responses, appetiteLoss: hasAppetiteLoss };
    setResponses(updatedResponses);
    setCurrentStep('results');
  };

  const handleResultsConfirm = () => {
    const result = evaluateVitality(responses);
    onComplete(result);
    setCurrentStep('completed');
  };

  const handleCarerCorrection = (weightCorrect: boolean, appetiteCorrect: boolean) => {
    // 基于照护者修正重新评估营养状况
    const weightProblem = !weightCorrect;
    const appetiteProblem = !appetiteCorrect;
    const anyNutritionProblem = weightProblem || appetiteProblem;
    
    const correctedResult: DomainResult = {
      status: anyNutritionProblem ? 'FAIL' : 'PASS',
      completionMethod: 'carer_corrected',
      testResults: [
        {
          name: "体重减轻评估",
          passed: weightCorrect,
          response: !weightCorrect,
          carerCorrected: true,
          rawUserAnswers: {
            weightLoss: !weightCorrect,
            weightLossPresent: !weightCorrect,
            carerCorrected: true
          }
        },
        {
          name: "食欲减退评估", 
          passed: appetiteCorrect,
          response: !appetiteCorrect,
          carerCorrected: true,
          rawUserAnswers: {
            appetiteLoss: !appetiteCorrect,
            appetiteLossPresent: !appetiteCorrect,
            carerCorrected: true
          }
        }
      ],
      recommendations: anyNutritionProblem ?
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C7-S7.2-CommunityIntervention"] :
        ["ICOPE-C3-S3.1.3-Advice-HealthyDiet"]
    };
    
    onComplete(correctedResult);
    setCurrentStep('completed');
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => {
        onNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const renderIntro = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">营养状况评估</h2>
        <p className="text-gray-600">评估您的营养健康状态</p>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">关于营养状况评估：</h3>
        <div className="space-y-3 text-gray-700">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
            <p>营养不良是老年人常见的健康风险</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
            <p>体重变化和食欲改变是重要的预警信号</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
            <p>及早发现有助于及时调整饮食习惯</p>
          </div>
        </div>
        
        <button
          onClick={() => setCurrentStep('weight_loss')}
          className="mt-6 w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors"
        >
          开始评估
        </button>
      </div>
    </div>
  );

  const renderWeightLoss = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">体重变化评估</h2>
        <p className="text-gray-600">第1个问题，共2个问题</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">请仔细回答以下问题：</h3>
          <p className="text-xl text-gray-800 mb-2">
            过去3个月内，您是否非意愿性地体重减轻超过3公斤？
          </p>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• "非意愿性"是指：不是因为主动节食或锻炼导致的体重下降</p>
            <p>• 包括：因为疾病、食欲不振、消化问题等原因导致的体重减轻</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => handleWeightLoss(true)}
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">是的，我体重减轻超过3公斤</span>
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-red-600 mt-1">非意愿性体重减轻可能提示营养问题</p>
          </button>
          
          <button
            onClick={() => handleWeightLoss(false)}
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800">没有，我的体重基本稳定</span>
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-green-600 mt-1">体重稳定通常是营养状况良好的表现</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderAppetiteLoss = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">食欲状况评估</h2>
        <p className="text-gray-600">第2个问题，共2个问题</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">请回答以下问题：</h3>
          <p className="text-xl text-gray-800 mb-2">
            您是否有过食欲减退的经历？
          </p>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• 食欲减退包括：对食物失去兴趣、进食量明显减少</p>
            <p>• 可能表现为：不想吃东西、吃得很少就饱了、对以前喜欢的食物失去兴趣</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => handleAppetiteLoss(true)}
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">是的，我有食欲减退的情况</span>
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-red-600 mt-1">食欲减退可能影响营养摄入</p>
          </button>
          
          <button
            onClick={() => handleAppetiteLoss(false)}
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800">没有，我的食欲正常</span>
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-green-600 mt-1">正常食欲有助于保持良好营养状况</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    const weightLossPresent = responses.weightLoss === true;
    const appetiteLossPresent = responses.appetiteLoss === true;
    const anyProblem = weightLossPresent || appetiteLossPresent;
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            anyProblem ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            <svg className={`w-8 h-8 ${anyProblem ? 'text-yellow-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">营养状况评估结果</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">评估详情：</h3>
          
          <div className="space-y-3 mb-6">
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              weightLossPresent ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}>
              <span className="font-medium">体重减轻评估</span>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${weightLossPresent ? 'text-red-700' : 'text-green-700'}`}>
                  {weightLossPresent ? '有体重减轻' : '体重稳定'}
                </span>
                <svg className={`w-4 h-4 ${weightLossPresent ? 'text-red-600' : 'text-green-600'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              appetiteLossPresent ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}>
              <span className="font-medium">食欲状况评估</span>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${appetiteLossPresent ? 'text-red-700' : 'text-green-700'}`}>
                  {appetiteLossPresent ? '有食欲减退' : '食欲正常'}
                </span>
                <svg className={`w-4 h-4 ${appetiteLossPresent ? 'text-red-600' : 'text-green-600'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${
            anyProblem ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'
          }`}>
            <div className="flex items-center space-x-2 mb-2">
              <svg className={`w-5 h-5 ${anyProblem ? 'text-yellow-600' : 'text-green-600'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className={`font-medium ${anyProblem ? 'text-yellow-800' : 'text-green-800'}`}>
                {anyProblem ? '营养状况需要关注' : '营养状况良好'}
              </span>
            </div>
            <p className={`text-sm ${anyProblem ? 'text-yellow-700' : 'text-green-700'}`}>
              {anyProblem 
                ? '建议关注饮食营养，考虑咨询营养专家或医生' 
                : '请继续保持良好的饮食习惯，确保营养均衡'}
            </p>
          </div>
          
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              确认结果，完成测试
            </button>
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>照护者修正答案</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const systemResult = evaluateVitality(responses);
    const weightLossPresent = responses.weightLoss === true;
    const appetiteLossPresent = responses.appetiteLoss === true;
    const anyProblem = weightLossPresent || appetiteLossPresent;
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className="space-y-3 mb-4">
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  weightLossPresent ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                }`}>
                  <span className="font-medium">体重减轻评估</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${weightLossPresent ? 'text-red-700' : 'text-green-700'}`}>
                      {weightLossPresent ? '有体重减轻' : '体重稳定'}
                    </span>
                    <span className={`text-lg ${weightLossPresent ? 'text-red-600' : 'text-green-600'}`}>
                      {weightLossPresent ? '❌' : '✅'}
                    </span>
                  </div>
                </div>
                
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  appetiteLossPresent ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                }`}>
                  <span className="font-medium">食欲状况评估</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${appetiteLossPresent ? 'text-red-700' : 'text-green-700'}`}>
                      {appetiteLossPresent ? '有食欲减退' : '食欲正常'}
                    </span>
                    <span className={`text-lg ${appetiteLossPresent ? 'text-red-600' : 'text-green-600'}`}>
                      {appetiteLossPresent ? '❌' : '✅'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center ${anyProblem ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <span className={`font-medium ${anyProblem ? 'text-yellow-800' : 'text-green-800'}`}>
                  系统评估：{anyProblem ? '❌ 营养状况需要关注' : '✅ 营养状况良好'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据对老年人的观察和了解，确认以下各项的实际情况：
            </p>
            
            <div className="space-y-4">
              {/* 体重状况确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">体重状况确认</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, weightCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.weightCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.weightCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.weightCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 体重正常，没有非意愿性减轻</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, weightCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.weightCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.weightCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.weightCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 有明显体重减轻问题</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 食欲状况确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">食欲状况确认</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, appetiteCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.appetiteCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.appetiteCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.appetiteCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 食欲正常，进食良好</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, appetiteCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.appetiteCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.appetiteCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.appetiteCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 有食欲减退问题</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(carerCorrection.weightCorrect!, carerCorrection.appetiteCorrect!)}
            disabled={carerCorrection.weightCorrect === null || carerCorrection.appetiteCorrect === null}
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">营养状况评估完成</h2>
      <p className="text-gray-600">正在进入下一项评估...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {currentStep === 'intro' && renderIntro()}
        {currentStep === 'weight_loss' && renderWeightLoss()}
        {currentStep === 'appetite_loss' && renderAppetiteLoss()}
        {currentStep === 'results' && renderResults()}
        {currentStep === 'carer_correction' && renderCarerCorrection()}
        {currentStep === 'completed' && renderCompleted()}
      </div>
    </div>
  );
};

export default VitalityAssessment;